import { useState, useEffect } from 'react';
import { format, addDays, startOfToday, isBefore, isToday, getDay, addMonths } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { getAvailableSlots, createBooking, getPublicSettings } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { Phone, Mail, User, Clock, Calendar as CalendarIcon, Car, ChevronRight, ChevronLeft, Info, MapPin, Building, Ban, CheckCircle2, Cog, Fuel, Wrench, Moon } from 'lucide-react';
import Navigation from '@/components/Navigation';
import ChatBot from '@/components/ChatBot';
import AfterHoursEnquiryDialog from '@/components/AfterHoursEnquiryDialog';

export default function HomePage() {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    business_name: '',
    vehicle_make: '',
    vehicle_model: '',
    vehicle_year: '',
    vehicle_registration: '',
    notes: '',
    // Consent
    social_media_consent: false,
    // Technical
    axle_type: '',
    stud_pattern: '',
    // Engine
    fuel_system: '',
    ecu_type: '',
    carb_type: '',
    aspiration: '',
    mod_list: '',
    previous_tune: '',
    known_faults: '',
  });
  const [formStep, setFormStep] = useState(1); // 1..3

  const [dayType, setDayType] = useState(null);
  const [bookingsClosed, setBookingsClosed] = useState(false);
  const [closedMessage, setClosedMessage] = useState('');
  const [showAfterHours, setShowAfterHours] = useState(false);

  useEffect(() => {
    getPublicSettings()
      .then((res) => {
        const d = res.data || {};
        if (d.bookings_enabled === false) {
          setBookingsClosed(true);
          setClosedMessage(d.closed_message || '');
        }
      })
      .catch(() => {});
  }, []);

  const today = startOfToday();
  const maxDate = addMonths(today, 12); // Full year ahead

  // Get day type description
  const getDayTypeDescription = (date) => {
    if (!date) return null;
    const dayOfWeek = getDay(date); // 0=Sunday, 6=Saturday
    if (dayOfWeek === 0) {
      return { type: 'closed', label: 'Closed Sundays', slots: null };
    }
    return {
      type: 'weekday',
      label: 'Dyno Sessions',
      slots: '8:00am-11:00am, 11:00am-1:00pm, 1:30pm-5:00pm',
    };
  };

  // Fetch available slots when date changes
  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots(format(selectedDate, 'yyyy-MM-dd'));
    }
  }, [selectedDate]);

  const fetchAvailableSlots = async (date) => {
    setLoading(true);
    setSelectedSlot(null);
    setDayType(getDayTypeDescription(selectedDate));
    try {
      const response = await getAvailableSlots(date);
      setAvailableSlots(response.data.available || []);
      if (response.data.day_type) {
        setDayType(getDayTypeDescription(selectedDate));
      }
    } catch (error) {
      toast.error('Failed to fetch available slots');
      setAvailableSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    setShowBookingForm(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedSlot) return;

    setSubmitting(true);
    try {
      const bookingData = {
        date: format(selectedDate, 'yyyy-MM-dd'),
        slot_id: selectedSlot.id,
        customer: formData,
      };

      const response = await createBooking(bookingData);
      toast.success('Booking submitted for approval!');
      setShowBookingForm(false);
      setFormStep(1);
      navigate(`/booking/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setSubmitting(false);
    }
  };

  const canProceedFromStep = (step) => {
    if (step === 1) {
      return (
        formData.name.trim() &&
        formData.email.trim() &&
        formData.phone.trim() &&
        formData.address.trim() &&
        formData.vehicle_make.trim() &&
        formData.vehicle_model.trim() &&
        formData.vehicle_year.trim()
      );
    }
    if (step === 2) {
      return formData.axle_type.trim() && formData.stud_pattern.trim();
    }
    if (step === 3) {
      if (!formData.fuel_system || !formData.aspiration) return false;
      if (formData.fuel_system === 'EFI' && !formData.ecu_type) return false;
      if (formData.fuel_system === 'Carby' && !formData.carb_type) return false;
      return true;
    }
    return false;
  };

  const isDateDisabled = (date) => {
    if (isBefore(date, today)) return true;
    // Open Mon–Sat, closed Sundays
    const dayOfWeek = getDay(date); // 0=Sunday, 6=Saturday
    if (dayOfWeek === 0) return true;
    return false;
  };

  // Custom day content to show availability
  const getDayClassName = (date) => {
    const dayOfWeek = getDay(date);
    if (dayOfWeek === 0) return '';
    return 'ring-2 ring-green-500/30'; // Mon–Sat open
  };

  return (
    <div className="min-h-screen bg-zinc-950 relative overflow-hidden">
      {/* Ghost Text Background */}
      <div className="ghost-text">NAKI TUNE 2.0</div>
      
      {/* Background Image Overlay */}
      <div 
        className="absolute inset-0 z-0 opacity-10"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1752774579270-523a9e91e6d4?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      
      {/* Navigation */}
      <Navigation />

      {/* Main Content */}
      <main className="relative z-10 container mx-auto px-4 py-8 md:py-16">
        {/* Hero Section */}
        <div className="text-center mb-12 animate-fade-in-up">
          <h2 className="font-['Barlow_Condensed'] text-4xl md:text-6xl font-black uppercase tracking-tighter text-white italic mb-4">
            Book Your Dyno Session
          </h2>
          <p className="text-zinc-400 text-lg max-w-xl mx-auto">
            Open Monday to Saturday, 8:00 AM – 5:00 PM (lunch break 1:00 PM – 1:30 PM).
            After-hours dyno tuning available by arrangement.
          </p>
        </div>

        {bookingsClosed && (
          <div
            className="max-w-3xl mx-auto mb-10 p-6 border-2 border-red-800/60 bg-red-950/30 flex flex-col md:flex-row items-center gap-4 text-center md:text-left"
            data-testid="bookings-closed-banner"
          >
            <div className="w-14 h-14 bg-red-900/50 rounded-sm flex items-center justify-center flex-shrink-0">
              <Ban className="w-7 h-7 text-red-400" />
            </div>
            <div className="flex-1">
              <p className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wide text-red-300">
                Bookings Temporarily Closed
              </p>
              <p className="text-zinc-300 text-sm mt-1">
                {closedMessage || 'Sorry, we are not accepting new bookings right now. Please check back soon or call us to enquire.'}
              </p>
            </div>
          </div>
        )}

        {/* Booking Section */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 max-w-6xl mx-auto">
          {/* Calendar */}
          <div className="lg:col-span-5 animate-fade-in-up animation-delay-100">
            <div className="glass p-6 border border-zinc-800 bg-zinc-900/50">
              <div className="flex items-center gap-2 mb-6">
                <CalendarIcon className="w-5 h-5 text-green-500" />
                <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wide text-white">
                  Select Date
                </h3>
              </div>
              <div className="flex justify-center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={isDateDisabled}
                  className="rounded-sm border border-zinc-800"
                  data-testid="booking-calendar"
                />
              </div>
              <p className="text-xs text-zinc-500 mt-4 text-center">
                Book up to 12 months ahead - Monday to Saturday
              </p>
              <div className="mt-4 space-y-2 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500/30 border border-green-500/50"></div>
                  <span className="text-zinc-400">Mon – Sat · 8am – 5pm</span>
                </div>
                <p className="text-zinc-500 pl-5">
                  Lunch break 1:00 – 1:30pm · After-hours by arrangement
                </p>
              </div>
            </div>
          </div>

          {/* Time Slots */}
          <div className="lg:col-span-7 animate-fade-in-up animation-delay-200">
            <div className="glass p-6 border border-zinc-800 bg-zinc-900/50">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-5 h-5 text-green-500" />
                <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wide text-white">
                  Available Slots
                </h3>
              </div>

              {/* Day Type Info */}
              {selectedDate && dayType && (
                <div className="mb-4 p-3 border border-green-500/30 bg-green-500/10">
                  <div className="flex items-center gap-2">
                    <Info className="w-4 h-4 text-zinc-300" />
                    <span className="text-sm text-zinc-300 font-medium">{dayType.label}</span>
                  </div>
                  {dayType.slots && (
                    <p className="text-xs text-zinc-500 mt-1 ml-6">{dayType.slots}</p>
                  )}
                </div>
              )}

              {!selectedDate ? (
                <div className="text-center py-12 text-zinc-500">
                  <CalendarIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Select a date to view available time slots</p>
                </div>
              ) : loading ? (
                <div className="text-center py-12">
                  <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-zinc-500">Loading slots...</p>
                </div>
              ) : availableSlots.length === 0 ? (
                <div className="text-center py-12 text-zinc-500">
                  <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No slots available for this date</p>
                  <p className="text-sm mt-2">Please select another date</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="time-slots">
                  {availableSlots.map((slot) => (
                    <button
                      key={slot.id}
                      onClick={() => handleSlotSelect(slot)}
                      className="slot-card p-4 border border-zinc-800 bg-zinc-900/50 hover:bg-green-600/10 hover:border-green-600 text-left group"
                      data-testid={`slot-${slot.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-['JetBrains_Mono'] text-lg text-white font-medium">
                            {slot.label}
                          </p>
                          {slot.note && (
                            <p className="text-xs text-zinc-500 mt-1">{slot.note}</p>
                          )}
                        </div>
                        <ChevronRight className="w-5 h-5 text-zinc-600 group-hover:text-green-500 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* After-Hours CTA */}
        <div className="mt-10 max-w-3xl mx-auto animate-fade-in-up animation-delay-200">
          <button
            type="button"
            onClick={() => setShowAfterHours(true)}
            className="w-full glass p-5 border border-green-800/50 bg-green-950/20 hover:bg-green-950/40 hover:border-green-600/60 transition-colors text-left group"
            data-testid="after-hours-cta"
          >
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 flex items-center justify-center rounded-sm bg-green-900/40 text-green-400 flex-shrink-0">
                <Moon className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="text-white font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-wide">
                  Need an after-hours slot?
                </p>
                <p className="text-zinc-400 text-sm mt-0.5">
                  Can't make Mon–Sat 8am–5pm? Send through your details and William will arrange a time to suit.
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-green-500 group-hover:translate-x-1 transition-transform flex-shrink-0" />
            </div>
          </button>
        </div>

        {/* Contact Section */}
        <div className="mt-16 text-center animate-fade-in-up animation-delay-300">
          <div className="inline-block glass p-6 border border-zinc-800 bg-zinc-900/50">
            <p className="text-xs text-zinc-500 uppercase tracking-[0.15em] mb-4">
              Need to cancel or reschedule? Contact us
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <a 
                href="tel:0278150446" 
                className="flex items-center gap-2 text-white hover:text-green-500 transition-colors"
                data-testid="contact-phone"
              >
                <Phone className="w-4 h-4" />
                <span className="font-['JetBrains_Mono']">William: 0278150446</span>
              </a>
              <a 
                href="mailto:Nakitune@outlook.co.nz" 
                className="flex items-center gap-2 text-white hover:text-green-500 transition-colors"
                data-testid="contact-email"
              >
                <Mail className="w-4 h-4" />
                <span className="font-['JetBrains_Mono']">Nakitune@outlook.co.nz</span>
              </a>
            </div>
          </div>
        </div>
      </main>

      {/* After-Hours Enquiry Dialog */}
      <AfterHoursEnquiryDialog open={showAfterHours} onOpenChange={setShowAfterHours} />

      {/* Booking Form Dialog */}
      <Dialog open={showBookingForm} onOpenChange={setShowBookingForm}>
        <DialogContent className="sm:max-w-lg bg-zinc-900 border-zinc-800 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight text-white">
              Complete Your Booking
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {selectedDate && selectedSlot && (
                <span className="font-['JetBrains_Mono'] text-green-500">
                  {format(selectedDate, 'EEEE, MMMM d, yyyy')} • {selectedSlot.label}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {/* Progress indicator */}
          <div className="flex items-center justify-center gap-2 mt-4 mb-2" data-testid="booking-progress">
            {[1, 2, 3].map((n) => (
              <div key={n} className="flex items-center">
                <div
                  className={`w-8 h-8 flex items-center justify-center border font-mono text-sm ${
                    n < formStep
                      ? 'bg-green-600 border-green-500 text-white'
                      : n === formStep
                      ? 'bg-zinc-950 border-green-500 text-green-500'
                      : 'bg-zinc-950 border-zinc-700 text-zinc-500'
                  }`}
                  data-testid={`step-indicator-${n}`}
                >
                  {n < formStep ? <CheckCircle2 className="w-4 h-4" /> : n}
                </div>
                {n < 3 && (
                  <div className={`w-8 h-0.5 mx-1 ${n < formStep ? 'bg-green-600' : 'bg-zinc-800'}`} />
                )}
              </div>
            ))}
          </div>
          <p className="text-center text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">
            {formStep === 1 ? 'Step 1 of 3 — Basic Info' : formStep === 2 ? 'Step 2 of 3 — Technical' : 'Step 3 of 3 — Engine Specs'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* -------- STEP 1: Basic Info -------- */}
            {formStep === 1 && (
              <div className="space-y-4" data-testid="step-1-basic">
                <div className="flex items-center gap-2 text-zinc-400">
                  <User className="w-4 h-4" />
                  <span className="text-xs uppercase tracking-[0.15em]">Contact & Vehicle</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-zinc-300">Full Name *</Label>
                    <Input id="name" name="name" value={formData.name} onChange={handleInputChange} required className="bg-zinc-950 border-zinc-800 focus:border-green-600 font-mono" placeholder="John Smith" data-testid="input-name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-zinc-300">Phone *</Label>
                    <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleInputChange} required className="bg-zinc-950 border-zinc-800 focus:border-green-600 font-mono" placeholder="027 xxx xxxx" data-testid="input-phone" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-zinc-300">Email *</Label>
                  <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} required className="bg-zinc-950 border-zinc-800 focus:border-green-600 font-mono" placeholder="your@email.com" data-testid="input-email" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address" className="text-zinc-300 flex items-center gap-2"><MapPin className="w-3 h-3" />Address *</Label>
                  <Input id="address" name="address" value={formData.address} onChange={handleInputChange} required className="bg-zinc-950 border-zinc-800 focus:border-green-600 font-mono" placeholder="123 Main St, City" data-testid="input-address" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="business_name" className="text-zinc-300 flex items-center gap-2"><Building className="w-3 h-3" />Business Name (Optional)</Label>
                  <Input id="business_name" name="business_name" value={formData.business_name} onChange={handleInputChange} className="bg-zinc-950 border-zinc-800 focus:border-green-600 font-mono" placeholder="Your business name" data-testid="input-business" />
                </div>

                <div className="pt-4 border-t border-zinc-800 space-y-4">
                  <div className="flex items-center gap-2 text-zinc-400">
                    <Car className="w-4 h-4" />
                    <span className="text-xs uppercase tracking-[0.15em]">Vehicle Type</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="vehicle_year" className="text-zinc-300">Year *</Label>
                      <Input id="vehicle_year" name="vehicle_year" value={formData.vehicle_year} onChange={handleInputChange} required className="bg-zinc-950 border-zinc-800 focus:border-green-600 font-mono" placeholder="1995" data-testid="input-year" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="vehicle_make" className="text-zinc-300">Make *</Label>
                      <Input id="vehicle_make" name="vehicle_make" value={formData.vehicle_make} onChange={handleInputChange} required className="bg-zinc-950 border-zinc-800 focus:border-green-600 font-mono" placeholder="Nissan" data-testid="input-make" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="vehicle_model" className="text-zinc-300">Model *</Label>
                      <Input id="vehicle_model" name="vehicle_model" value={formData.vehicle_model} onChange={handleInputChange} required className="bg-zinc-950 border-zinc-800 focus:border-green-600 font-mono" placeholder="Skyline R33" data-testid="input-model" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vehicle_registration" className="text-zinc-300">Registration (Optional)</Label>
                    <Input id="vehicle_registration" name="vehicle_registration" value={formData.vehicle_registration} onChange={handleInputChange} className="bg-zinc-950 border-zinc-800 focus:border-green-600 font-mono" placeholder="ABC123" data-testid="input-registration" />
                  </div>
                </div>

                <div className="pt-4 border-t border-zinc-800">
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="social-consent-label">
                    <Checkbox
                      checked={formData.social_media_consent}
                      onCheckedChange={(v) => setFormData((p) => ({ ...p, social_media_consent: !!v }))}
                      className="mt-0.5 border-zinc-600 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-500"
                      data-testid="input-social-consent"
                    />
                    <span className="text-sm text-zinc-300">
                      I consent to photos/videos of my vehicle being shared on NAKI TUNE 2.0 social media.
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* -------- STEP 2: Technical -------- */}
            {formStep === 2 && (
              <div className="space-y-4" data-testid="step-2-technical">
                <div className="flex items-center gap-2 text-zinc-400">
                  <Cog className="w-4 h-4" />
                  <span className="text-xs uppercase tracking-[0.15em]">Technical</span>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Axle Type *</Label>
                  <RadioGroup value={formData.axle_type} onValueChange={(v) => setFormData((p) => ({ ...p, axle_type: v }))} className="grid grid-cols-2 gap-2" data-testid="axle-radio-group">
                    {['Live Axle', 'IRS', 'Solid Axle', 'Other'].map((opt) => (
                      <label key={opt} className={`p-3 border cursor-pointer transition-colors ${formData.axle_type === opt ? 'border-green-500 bg-green-900/30 text-green-200' : 'border-zinc-700 text-zinc-300 hover:border-zinc-500'}`}>
                        <RadioGroupItem value={opt} className="sr-only" />
                        <span className="text-sm">{opt}</span>
                      </label>
                    ))}
                  </RadioGroup>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Stud Pattern *</Label>
                  <RadioGroup value={formData.stud_pattern} onValueChange={(v) => setFormData((p) => ({ ...p, stud_pattern: v }))} className="grid grid-cols-2 gap-2" data-testid="stud-radio-group">
                    {['4x100', '5x114.3', '6x139.7', '5x100', '5x120', 'Other'].map((opt) => (
                      <label key={opt} className={`p-3 border cursor-pointer transition-colors ${formData.stud_pattern === opt ? 'border-green-500 bg-green-900/30 text-green-200' : 'border-zinc-700 text-zinc-300 hover:border-zinc-500'}`}>
                        <RadioGroupItem value={opt} className="sr-only" />
                        <span className="text-sm font-mono">{opt}</span>
                      </label>
                    ))}
                  </RadioGroup>
                </div>
              </div>
            )}

            {/* -------- STEP 3: Engine Specs -------- */}
            {formStep === 3 && (
              <div className="space-y-4" data-testid="step-3-engine">
                <div className="flex items-center gap-2 text-zinc-400">
                  <Wrench className="w-4 h-4" />
                  <span className="text-xs uppercase tracking-[0.15em]">Engine Specs</span>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Fuel System *</Label>
                  <RadioGroup value={formData.fuel_system} onValueChange={(v) => setFormData((p) => ({ ...p, fuel_system: v, ecu_type: '', carb_type: '' }))} className="grid grid-cols-2 gap-2" data-testid="fuel-radio-group">
                    {['EFI', 'Carby'].map((opt) => (
                      <label key={opt} className={`p-3 border cursor-pointer transition-colors ${formData.fuel_system === opt ? 'border-green-500 bg-green-900/30 text-green-200' : 'border-zinc-700 text-zinc-300 hover:border-zinc-500'}`}>
                        <RadioGroupItem value={opt} className="sr-only" />
                        <span className="text-sm font-['Barlow_Condensed'] uppercase tracking-wide">{opt}</span>
                      </label>
                    ))}
                  </RadioGroup>
                </div>
                {formData.fuel_system === 'EFI' && (
                  <div className="space-y-2" data-testid="ecu-field">
                    <Label className="text-zinc-300">ECU *</Label>
                    <Select value={formData.ecu_type} onValueChange={(v) => setFormData((p) => ({ ...p, ecu_type: v }))}>
                      <SelectTrigger className="bg-zinc-950 border-zinc-800 focus:border-green-600 font-mono" data-testid="ecu-select">
                        <SelectValue placeholder="Select ECU" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                        {['Standard', 'Link', 'Maxx', 'Haltech', 'Motec', 'Other'].map((o) => (
                          <SelectItem key={o} value={o}>{o}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {formData.fuel_system === 'Carby' && (
                  <div className="space-y-2" data-testid="carb-field">
                    <Label className="text-zinc-300">Carburettor *</Label>
                    <Select value={formData.carb_type} onValueChange={(v) => setFormData((p) => ({ ...p, carb_type: v }))}>
                      <SelectTrigger className="bg-zinc-950 border-zinc-800 focus:border-green-600 font-mono" data-testid="carb-select">
                        <SelectValue placeholder="Select carburettor" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                        {['Holley', 'Edelbrock', 'Other'].map((o) => (
                          <SelectItem key={o} value={o}>{o}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="text-zinc-300">Aspiration *</Label>
                  <RadioGroup value={formData.aspiration} onValueChange={(v) => setFormData((p) => ({ ...p, aspiration: v }))} className="grid grid-cols-2 gap-2" data-testid="aspiration-radio-group">
                    {['NA', 'Boosted'].map((opt) => (
                      <label key={opt} className={`p-3 border cursor-pointer transition-colors ${formData.aspiration === opt ? 'border-green-500 bg-green-900/30 text-green-200' : 'border-zinc-700 text-zinc-300 hover:border-zinc-500'}`}>
                        <RadioGroupItem value={opt} className="sr-only" />
                        <span className="text-sm font-['Barlow_Condensed'] uppercase tracking-wide">{opt}</span>
                      </label>
                    ))}
                  </RadioGroup>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mod_list" className="text-zinc-300">Mod List</Label>
                  <Textarea id="mod_list" name="mod_list" value={formData.mod_list} onChange={handleInputChange} className="bg-zinc-950 border-zinc-800 focus:border-green-600 font-mono min-h-[80px]" placeholder="Turbo, intercooler, injectors, cams..." data-testid="input-mod-list" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="previous_tune" className="text-zinc-300">Previous Tune</Label>
                  <Input id="previous_tune" name="previous_tune" value={formData.previous_tune} onChange={handleInputChange} className="bg-zinc-950 border-zinc-800 focus:border-green-600 font-mono" placeholder="e.g., tuned by XYZ in 2023, or None" data-testid="input-previous-tune" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="known_faults" className="text-zinc-300">Known Faults</Label>
                  <Textarea id="known_faults" name="known_faults" value={formData.known_faults} onChange={handleInputChange} className="bg-zinc-950 border-zinc-800 focus:border-green-600 font-mono min-h-[60px]" placeholder="Any misfires, oil leaks, coolant issues, etc." data-testid="input-known-faults" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes" className="text-zinc-300">Additional Notes (Optional)</Label>
                  <Textarea id="notes" name="notes" value={formData.notes} onChange={handleInputChange} className="bg-zinc-950 border-zinc-800 focus:border-green-600 font-mono min-h-[60px]" placeholder="Anything else William should know..." data-testid="input-notes" />
                </div>
              </div>
            )}

            {/* Navigation buttons */}
            <div className="flex gap-2 pt-4 border-t border-zinc-800">
              {formStep > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setFormStep((s) => s - 1)}
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                  data-testid="btn-back"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" /> Back
                </Button>
              )}
              {formStep < 3 && (
                <Button
                  type="button"
                  onClick={() => setFormStep((s) => s + 1)}
                  disabled={!canProceedFromStep(formStep)}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold uppercase tracking-wider py-6 btn-glow disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="btn-next"
                >
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
              {formStep === 3 && (
                <Button
                  type="submit"
                  disabled={submitting || !canProceedFromStep(3)}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold uppercase tracking-wider py-6 btn-glow disabled:opacity-50"
                  data-testid="submit-booking"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Submitting...
                    </span>
                  ) : (
                    'Submit for Approval'
                  )}
                </Button>
              )}
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Chatbot */}
      <ChatBot />
    </div>
  );
}
